import {
  FetchFunction,
  GraphQLSingularResponse,
  isPromise,
  Observable,
  Subscribable,
} from "relay-runtime";

import createFetcher from "./createFetcher";
import Middleware, {
  GraphQLOperation,
  MiddlewareContext,
  IncomingRequestContext,
} from "./Middleware";

function compose(middleware: ReadonlyArray<Middleware>): Middleware {
  if (middleware.length === 0) {
    return (arg) => arg;
  }

  // We know the last item exists because the length is not zero!
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const last = middleware[middleware.length - 1]!;
  const rest = middleware.slice(0, -1);

  return (arg) => rest.reduceRight((composed, f) => f(composed), last(arg));
}

/**
 * Remove undefined values from any object and return empty object if input is undefined
 * @param obj - The object that may contain undefined values
 * @returns Sanitized object with no undefined values
 */
function sanitizeObject(obj: any): Record<string, any> {
  if (!obj || typeof obj !== "object") {
    return {};
  }

  const sanitized: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj as Record<string, any>)) {
    if (value !== undefined) {
      if (typeof value === "object" && value !== null) {
        // Recursively clean nested objects
        const cleanedValue = sanitizeObject(value);
        if (Object.keys(cleanedValue).length > 0) {
          sanitized[key] = cleanedValue;
        }
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        sanitized[key] = value;
      }
    }
  }

  return sanitized;
}

/**
 * Remove undefined values from incoming request context and return empty object if context is undefined
 * @param incomingRequestContext - The incoming request context that may contain undefined values
 * @returns Sanitized context object with no undefined values
 */
function sanitizeIncomingRequestContext(
  incomingRequestContext?: IncomingRequestContext,
): Partial<IncomingRequestContext> {
  return sanitizeObject(
    incomingRequestContext,
  ) as Partial<IncomingRequestContext>;
}

export default function createMiddlewareStack({
  url,
  middleware,
  options = {},
  incomingRequestContext,
}: {
  url: string;
  middleware: ReadonlyArray<Middleware>;
  options?: {
    onHeaders?: (headers: Headers, operation: GraphQLOperation) => void;
  };
  incomingRequestContext?: IncomingRequestContext;
}): FetchFunction {
  const fetcher = createFetcher(options);
  const stack = compose(middleware)(fetcher);
  const sanitizedIncomingRequestContext = sanitizeIncomingRequestContext(
    incomingRequestContext,
  );

  return (
    request,
    variables,
    cacheConfig,
  ): Subscribable<GraphQLSingularResponse> => {
    if (!request.id) {
      // This should always be there when using persisted queries.
      throw new Error("Missing request id");
    }

    // Relay requires Observables for some of its features like polling.
    // Context: https://github.com/relay-tools/react-relay-network-modern/issues/40
    return Observable.create((observer) => {
      // Create the initial context that will be used for future requests.
      const ctx: MiddlewareContext = {
        url,
        fetchOpts: {
          method: "POST",
          headers: {},
        },
        request,
        operation: {
          operationName: request.name,
          variables,
        },
        cacheConfig,
        incomingRequestContext: sanitizedIncomingRequestContext,
      };

      const value = stack(ctx);

      // Return value directly if it's not a promise.
      if (!isPromise(value)) {
        observer.next(value);
        observer.complete();
        return;
      }

      value.then(
        (data) => {
          observer.next(data);
          observer.complete();
        },
        (e: Error) => {
          observer.error(e);
          observer.complete();
        },
      );
    });
  };
}
